import React from 'react';
import {t} from '@lingui/core/macro';
import {Trans} from '@lingui/react';
import cn from 'classnames';

import {useServerAgentConnection} from 'features/server/model/useServerAgentConnection';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';
import Copying from 'shared/ui/icons/Copying';
import {useCopyToClipboard} from 'shared/lib/hooks/useCopyToClipboard';

import styles from './ServerAgentConnectionScreen.module.scss';

interface IServerAgentConnectionScreenProps {
    token: string,
    serverUuid: string,
    variant?: 'install' | 'setToken',
    onDone: () => void,
}

const ServerAgentConnectionScreen: React.FC<IServerAgentConnectionScreenProps> = ({
    token,
    serverUuid,
    variant = 'install',
    onDone,
}) => {
    const {copy} = useCopyToClipboard();

    const {command, isConnected} = useServerAgentConnection({token, serverUuid, variant});

    return (
        <>
            <ModalBodyWrapper className={styles.wrapper}>
                <p className={styles.text}>
                    {
                        variant === 'setToken'
                            ? (
                                <Trans
                                    id="server.agentConnection.descriptionSetToken"
                                    // eslint-disable-next-line max-len
                                    message="Выполните эту команду на сервере, чтобы заменить токен агента мониторинга на новый."
                                />
                            )
                            : (
                                <Trans
                                    id="server.agentConnection.description"
                                    message="Выполните эту команду на сервере, чтобы установить агента мониторинга."
                                />
                            )
                    }
                </p>
                <div className={styles.commandBlock}>
                    <pre className={styles.command}>{command}</pre>
                    <button
                        type="button"
                        className={styles.copyButton}
                        onClick={() => copy(command)}
                        aria-label={t`Скопировать команду`}
                    >
                        <Copying/>
                    </button>
                </div>
                <div
                    className={cn(styles.status, {[styles.statusConnected]: isConnected})}
                    role="status"
                    aria-live="polite"
                >
                    <span className={styles.indicator}>
                        <span className={styles.indicatorRing}/>
                        <span className={styles.indicatorSparks}/>
                        <span className={styles.indicatorCircle}>
                            <svg className={styles.indicatorCheck} viewBox="0 0 16 16" aria-hidden="true">
                                <path d="M4.5 8.3l2.3 2.3 4.7-4.8"/>
                            </svg>
                        </span>
                    </span>
                    <span key={isConnected ? 'connected' : 'waiting'} className={styles.statusText}>
                        {isConnected ? (
                            <Trans
                                id="server.agentConnection.success"
                                message="Агент успешно установлен, данные поступают!"
                            />
                        ) : (
                            <Trans
                                id="server.agentConnection.waiting"
                                message="Ожидаем данные от агента…"
                            />
                        )}
                    </span>
                </div>
            </ModalBodyWrapper>
            <ModalBlockButton
                primaryButtonText={t`Готово`}
                primaryButtonType="button"
                primaryButtonOnClick={onDone}
                primaryButtonDisabled={!isConnected}
                secondaryButtonText={t`Установить позже`}
                secondaryButtonOnClick={onDone}
            />
        </>
    );
};

export default ServerAgentConnectionScreen;
